/**
 * Joycart Infinite Scroll
 * Seamlessly loads more products as the user scrolls.
 * Falls back to standard pagination on error.
 */

(function() {
  class InfiniteScroll {
    constructor() {
      this.observer = null;
      this.loading = false;
      this.trigger = null;
      this.grid = null;
      this.fallbackPagination = null;
      this.init();
    }

    emitUpdate(detail = {}) {
      document.dispatchEvent(new CustomEvent('infinite-scroll:update', {
        detail: {
          grid: this.grid,
          trigger: this.trigger,
          ...detail
        }
      }));
    }
    
    init() {
      // Clean up previous observer
      if (this.observer) {
        this.observer.disconnect();
        this.observer = null;
      }

      this.trigger = document.querySelector('[data-infinite-scroll-trigger]');
      this.grid = document.querySelector('[data-infinite-scroll-grid], #ProductGrid');
      this.fallbackPagination = document.querySelector('[data-pagination-fallback]');
      
      if (!this.trigger || !this.grid) {
        // No trigger means we're on the last page or no pagination needed
        // Show fallback pagination if it exists (for non-JS / last page)
        if (this.fallbackPagination) {
          this.fallbackPagination.classList.remove('visually-hidden');
        }
        if (this.grid) {
          this.emitUpdate({
            status: 'ended',
            hasMore: false,
            appendedCount: 0,
            totalItems: this.grid.children.length
          });
        }
        return;
      }
      
      this.loading = false;

      // Hide the fallback pagination when infinite scroll is active
      if (this.fallbackPagination) {
        this.fallbackPagination.classList.add('visually-hidden');
      }
      
      this.observer = new IntersectionObserver((entries) => {
        if (entries[0].isIntersecting && !this.loading) {
          this.loadNextPage();
        }
      }, { rootMargin: '600px' });
      
      this.observer.observe(this.trigger);
    }
    
    async loadNextPage() {
      const nextUrl = this.trigger.dataset.nextUrl;
      if (!nextUrl || nextUrl === '' || nextUrl === 'null') return;
      
      this.loading = true;
      this.trigger.classList.add('loading');
      this.emitUpdate({
        status: 'loading',
        hasMore: true,
        totalItems: this.grid ? this.grid.children.length : 0
      });
      
      try {
        // Build the fetch URL: preserve current search params (sort, filters)
        // and append section_id for Shopify section rendering
        const currentParams = new URLSearchParams(window.location.search);
        const nextUrlObj = new URL(nextUrl, window.location.origin);
        
        // The nextUrl from Shopify already has ?page=N
        // Merge current sort/filter params into it (don't override page)
        for (const [key, value] of currentParams.entries()) {
          if (key !== 'page' && !nextUrlObj.searchParams.has(key)) {
            nextUrlObj.searchParams.set(key, value);
          }
        }
        
        // Add section_id for Shopify section rendering
        const sectionId = this.grid.dataset.id;
        if (sectionId) {
          nextUrlObj.searchParams.set('section_id', sectionId);
        }
        
        const response = await fetch(nextUrlObj.toString());
        if (!response.ok) throw new Error('Failed to load next page');
        
        const html = await response.text();
        const parser = new DOMParser();
        const doc = parser.parseFromString(html, 'text/html');
        
        const newGrid = doc.querySelector('[data-infinite-scroll-grid], #ProductGrid');
        const newTrigger = doc.querySelector('[data-infinite-scroll-trigger]');
        let appendedCount = 0;
        
        if (newGrid) {
          // Append new items
          const children = Array.from(newGrid.children);
          appendedCount = children.length;
          const fragment = document.createDocumentFragment();
          children.forEach(child => fragment.appendChild(child));
          this.grid.appendChild(fragment);
          
          // Re-init shimmers for new items
          if (window.initShimmers) window.initShimmers();
        }
        
        // Update trigger or remove it
        if (newTrigger && newTrigger.dataset.nextUrl) {
          this.trigger.dataset.nextUrl = newTrigger.dataset.nextUrl;
          this.trigger.dataset.currentPage = newTrigger.dataset.currentPage;
          this.trigger.dataset.totalPages = newTrigger.dataset.totalPages || this.trigger.dataset.totalPages;
          
          // Update URL bar to reflect current scroll position
          const cleanUrl = new URL(window.location.href);
          cleanUrl.searchParams.set('page', newTrigger.dataset.currentPage);
          history.replaceState(null, '', cleanUrl.toString());

          this.emitUpdate({
            status: 'appended',
            hasMore: true,
            appendedCount,
            currentPage: Number(this.trigger.dataset.currentPage || 0),
            totalPages: Number(this.trigger.dataset.totalPages || 0),
            totalItems: this.grid.children.length
          });
        } else {
          // No more pages — remove trigger and show a "loaded all" state
          this.trigger.remove();
          this.trigger = null;
          if (this.observer) {
            this.observer.disconnect();
            this.observer = null;
          }
          this.emitUpdate({
            status: 'ended',
            hasMore: false,
            appendedCount,
            totalItems: this.grid.children.length
          });
        }
        
      } catch (error) {
        console.error('Infinite Scroll Error:', error);
        this.emitUpdate({
          status: 'error',
          hasMore: false,
          error: String(error),
          totalItems: this.grid ? this.grid.children.length : 0
        });
        // On error, show fallback pagination so users can still navigate
        this.showFallbackPagination();
      } finally {
        this.loading = false;
        if (this.trigger) this.trigger.classList.remove('loading');
      }
    }

    showFallbackPagination() {
      if (this.fallbackPagination) {
        this.fallbackPagination.classList.remove('visually-hidden');
      }
      // Remove the broken trigger
      if (this.trigger) {
        this.trigger.remove();
        this.trigger = null;
      }
      if (this.observer) {
        this.observer.disconnect();
        this.observer = null;
      }
    }
  }

  // Initial init
  const scrollInstance = new InfiniteScroll();

  // Re-init on section load or dynamic updates
  document.addEventListener('shopify:section:load', () => scrollInstance.init());
  
  // Re-init on AJAX collection updates (sort/filter changes)
  document.addEventListener('collection:updated', () => {
    // Small delay to let DOM settle after AJAX swap
    requestAnimationFrame(() => {
      scrollInstance.init();
      if (window.initShimmers) window.initShimmers();
    });
  });



})();
